/**
 * Renders conflict resolution modal when server has newer data than client's pending update.
 * Must be rendered inside ConflictProvider, AlertProvider, GameProvider.
 */
import React, { useState } from 'react';
import { useConflict } from '../contexts/ConflictContext';
import { useGame } from '../contexts/GameContext';
import { useAlert } from '../contexts/AlertContext';
import ConflictResolutionModal from './ConflictResolutionModal';
import { removeFromQueue } from '../services/offlineQueue';
import { patchScheduleMatch, patchDashboardMatch } from '../api/readApi';
import * as matchApi from '../api/matchApi';

export default function ConflictModalRenderer() {
  const { conflicts, resolveConflict } = useConflict();
  const { gameId } = useGame();
  const gameIdStr = gameId || 'table-tennis';
  const { showAlert, showToast } = useAlert();
  const [overwriteInProgress, setOverwriteInProgress] = useState(false);
  const conflict = conflicts[0];

  const handleResolve = async (resolvedConflict, action) => {
    if (!resolvedConflict) return;
    if (action === 'useServer') {
      if (resolvedConflict.queueItemId) removeFromQueue(resolvedConflict.queueItemId);
      const serverMatch = resolvedConflict.serverMatch;
      if (serverMatch?.id) {
        patchScheduleMatch(gameIdStr, serverMatch.id, serverMatch);
        patchDashboardMatch(gameIdStr, serverMatch.id, serverMatch);
        window.dispatchEvent(new CustomEvent('scheduleCachePatched', { detail: { gameId: gameIdStr } }));
      }
      resolveConflict(resolvedConflict.id);
      return;
    }
    if (action === 'overwrite') {
      const payload = resolvedConflict.pendingPayload;
      if (!payload?.matchId) {
        showAlert('Cannot overwrite: missing match info.', { variant: 'error' });
        return;
      }
      setOverwriteInProgress(true);
      try {
        await matchApi.matchRescheduleComplete(
          payload.matchId,
          {
            scheduledAt: payload.scheduledAt,
            scheduledDate: payload.scheduledDate,
            scheduledTime: payload.scheduledTime,
            venueBlockId: payload.venueBlockId,
            forceOverwrite: true,
          },
          payload.actorName || ''
        );
        if (resolvedConflict.queueItemId) removeFromQueue(resolvedConflict.queueItemId);
        const matchUpdates = {
          scheduledAt: payload.scheduledAt,
          scheduledDate: payload.scheduledDate,
          scheduledTime: payload.scheduledTime,
          venueBlockId: payload.venueBlockId,
          rescheduleRequested: false,
          rescheduleApproved: false,
          updatedAt: Date.now(),
        };
        patchScheduleMatch(gameIdStr, payload.matchId, matchUpdates);
        patchDashboardMatch(gameIdStr, payload.matchId, matchUpdates);
        window.dispatchEvent(new CustomEvent('scheduleCachePatched', { detail: { gameId: gameIdStr } }));
        resolveConflict(resolvedConflict.id);
        showToast('Your version has been applied.', { variant: 'success' });
      } catch (err) {
        showAlert('Overwrite failed: ' + (err?.message || err), { variant: 'error' });
      } finally {
        setOverwriteInProgress(false);
      }
    }
  };

  return conflict ? (
    <ConflictResolutionModal
      conflict={conflict}
      onResolve={handleResolve}
      onClose={() => resolveConflict(conflict.id)}
      overwriteInProgress={overwriteInProgress}
    />
  ) : null;
}
